// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { useRef, useState, useCallback, useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Input, Menu, MenuItem, MenuDivider, MenuHeader, Icon, Status, Hint, Button } from '@plesk/ui-library';
import { keyCode, Locale } from 'jsw';
import { toFormData } from 'helpers/form';
import { addToast } from 'helpers/toasts';
import { useQuery, useMutation, useApolloClient, useLazyQuery } from '@apollo/client/react';
import { ServerError, ServerParseError } from '@apollo/client/errors';
import { isNetworkError } from 'helpers/isNetworkError';
import Link from 'components/common/Link';

import MAIN_HEADER_SEARCH_QUERY from 'queries/MainHeaderSearch.graphql';
import RECENT_SEARCH_HISTORY_QUERY from './RecentSearchHistoryQuery.graphql';
import DELETE_RECENT_SEARCH_RESULT_MUTATION from './DeleteRecentSearchResultMutation.graphql';

import KNOWLEDGE_SEARCH_QUERY from 'queries/KnowledgeSearch.graphql';


const Translate = Locale.getTranslate('components.search-bar');

const MENU_ITEM_CLASSNAME = 'main-header-search-result__item';

export const SEARCH_HISTORY_STORE_URL = '/cp/search/store';

const SEARCH_ACTION_DELAY = 300;
const KNOWLEDGE_SEARCH_ACTION_DELAY = 1500;

const removeTouchEventListeners = ({ onDocumentTouchStart, onDocumentTouchMove, onDocumentTouchEnd }) => {
    document.removeEventListener('touchstart', onDocumentTouchStart);
    document.removeEventListener('touchmove', onDocumentTouchMove);
    document.removeEventListener('touchend', onDocumentTouchEnd);
};

const MainHeaderSearch = ({ url }) => {
    const searchTermRef = useRef();
    const lastTermRef = useRef('');
    const lastKnowledgeTermRef = useRef('');
    const menuRef = useRef();
    const timer = useRef(null);
    const knowledgeTimer = useRef(null);
    const resetTimeout = useRef(null);
    const enterPressed = useRef(false);
    const pendingRequest = useRef(null);
    const skipKeyUp = useRef(false);
    const touchMoved = useRef(false);
    const { data: { config, viewer } = {} } = useQuery(MAIN_HEADER_SEARCH_QUERY);
    const [history, setHistory] = useState();
    const [deleteRecentSearchResult] = useMutation(DELETE_RECENT_SEARCH_RESULT_MUTATION, {
        onCompleted: ({ deleteRecentSearchResult }) => setHistory(deleteRecentSearchResult.query.recentSearch),
    });

    const knowledgesRequestAbortControllerRef = useRef(new AbortController());
    const abortKnowledgeSearch = () => {
        if (knowledgeTimer.current) {
            clearTimeout(knowledgeTimer.current);
            knowledgeTimer.current = null;
        }
        if (knowledgesRequestAbortControllerRef.current) {
            knowledgesRequestAbortControllerRef.current.abort();
            knowledgesRequestAbortControllerRef.current = null;
        }
    };

    const [getKnowledges] = useLazyQuery(KNOWLEDGE_SEARCH_QUERY);

    const [knowledges, setKnowledges] = useState([]);

    const [isSearchFocused, setSearchFocused] = useState(false);
    const [results, setResults] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);

    const [searchInProgress, setSearchInProgress] = useState({});
    const setSearchFlag = key => setSearchInProgress(prev => ({ ...prev, [key]: true }));
    const clearSearchFlag = key => setSearchInProgress(prev => ({ ...prev, [key]: false }));
    const isAnySearchInProgress = state => Object.values(state).some(Boolean);

    const apolloClient = useApolloClient();
    const isAdmin = viewer?.type === 'ADMIN';

    const fatalError = message => {
        // eslint-disable-next-line no-alert
        alert(message);
    };

    const handlePaste = () => scheduleSearch();

    const handleKeyUp = event => {
        if (skipKeyUp.current) {
            return;
        }
        if (event.keyCode === keyCode.ESC) {
            searchTermRef.current.blur();
            return;
        }
        if ([keyCode.UP_ARROW, keyCode.DOWN_ARROW, keyCode.LEFT_ARROW, keyCode.RIGHT_ARROW, keyCode.ENTER].indexOf(event.keyCode) !== -1) {
            return;
        }

        scheduleSearch();
    };

    const handleKeyDown = event => {
        skipKeyUp.current = event.ctrlKey || event.metaKey;

        if ([keyCode.UP_ARROW, keyCode.DOWN_ARROW].indexOf(event.keyCode) !== -1) {
            onArrowKeyPressed(event.keyCode);
            event.preventDefault();
        }

        enterPressed.current = (keyCode.ENTER === event.keyCode);
        if (enterPressed.current) {
            if (
                (pendingRequest.current && pendingRequest.current._complete)
                || (history && selectedItem !== null)
            ) {
                goToItem();
            } else {
                scheduleSearch();
            }
        }
    };

    const goToItem = () => {
        if (!menuRef.current) {
            return;
        }
        const elements = menuRef.current.querySelectorAll(`.${MENU_ITEM_CLASSNAME}`);
        const element = elements[selectedItem || 0];
        if (element) {
            element.click();
            searchTermRef.current.blur();
        }
    };

    const handleFocus = () => {
        if (resetTimeout.current) {
            clearTimeout(resetTimeout.current);
        }

        resetSearch();
        setSearchFocused(true);

        apolloClient.query({
            query: RECENT_SEARCH_HISTORY_QUERY,
        }).then(({ data }) => {
            setHistory(data.recentSearch);
        }).catch(error => {
            if (!ServerError.is(error) && !ServerParseError.is(error) && !isNetworkError(error)) {
                addToast({ intent: 'danger', message: error.message });
            }
        });

        document.addEventListener('touchstart', onDocumentTouchStart);
        document.addEventListener('touchmove', onDocumentTouchMove);
        document.addEventListener('touchend', onDocumentTouchEnd);
    };


    const handleBlur = () => {
        if (resetTimeout.current) {
            clearTimeout(resetTimeout.current);
        }

        lastTermRef.current = '';
        lastKnowledgeTermRef.current = '';

        resetTimeout.current = setTimeout(() => {
            resetSearch();
            setSearchFocused(false);
        }, 300);
    };

    const scheduleSearch = () => {
        setSearchFlag('simple');
        if (config?.knowledgeSearch?.enabled) {
            setSearchFlag('knowledge');
        }

        abortPreviousSearch();
        abortKnowledgeSearch();
        timer.current = setTimeout(findTerm, SEARCH_ACTION_DELAY);
        knowledgeTimer.current = setTimeout(findKnowledgeTerm, KNOWLEDGE_SEARCH_ACTION_DELAY);
    };

    const abortPreviousSearch = () => {
        if (pendingRequest.current) {
            const abort = pendingRequest.current.abort.bind(pendingRequest.current);
            // global state is cleared first due to onSearchComplete callback
            pendingRequest.current = null;
            abort();
        }

        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    };

    const onSearchSuccess = response => {
        if (lastTermRef.current !== response.request.options.parameters.term) {
            return;
        }

        let data;
        try {
            data = JSON.parse(response.responseText);
        } catch (e) {
            fatalError(`Failed to parse JSON response: ${e.message}`);
            return;
        }

        if ('error' === data.status) {
            const result = data.statusMessages.reduce((res, message) => `${res}${message.title}: ${message.content}\n`, '');
            fatalError(result);
            return;
        }

        setResults(data);
        if (enterPressed.current) {
            goToItem();
        } else if (data.records.length > 0) {
            setSelectedItem(0);
        }
    };

    const onSearchFailure = response => {
        fatalError(`Search request failed due to following error: ${response.responseText}`);
    };

    const onSearchComplete = response => {
        if (!response || pendingRequest.current === response.request) {
            clearSearchFlag('simple');
        }
    };

    const handleSaveRecentSearch = item => {
        const storeUrl = SEARCH_HISTORY_STORE_URL;
        const token = document.getElementById('forgery_protection_token').content;
        navigator.sendBeacon(storeUrl, toFormData({ ...item, forgery_protection_token: token }));
        flushRecentSearchCache();
    };

    const flushRecentSearchCache = () => {
        const { cache } = apolloClient;
        cache.evict({
            id: cache.identify({ __typename: 'Query' }),
            fieldName: 'recentSearch',
        });
        setHistory(null);
    };

    const renderSearchItems = (items, uatKey) => items.map(({ details, target, icon, link, title, label }, index) => {
        const iconUrl = (
            !icon ||
            icon.startsWith(Jsw.skinUrl) ||
            icon.startsWith('http://') ||
            icon.startsWith('https://') ||
            icon.startsWith('/modules/')
        ) ? icon : `${Jsw.skinUrl}${icon}`;

        return (
            <MenuItem
                data-action={uatKey}
                component={Link}
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                to={link}
                title={details}
                target={target}
                label={label}
                active={selectedItem === index}
                icon={iconUrl ? <Icon className="main-header-search-result__icon" src={iconUrl} /> : null}
                className={MENU_ITEM_CLASSNAME}
                onClick={e => {
                    e.stopPropagation();
                    handleSaveRecentSearch({ details, target, icon, link, title });
                }}
                id={`searchResultItem-${index}`}
            >
                {title}
            </MenuItem>
        );
    });

    const renderSearchResults = results => [
        results.records.length ? renderSearchItems(results.records, 'searchResultRegular') : (
            <div className="main-header-search-result__note main-header-search-result__note--empty">
                <Translate content="nothingFound" />
            </div>
        ),
        results.meta.moreResultsFound ? (
            <>
                <MenuDivider />
                <div className="main-header-search-result__note">
                    <Translate content="moreResultsFound" params={{ limit: config?.search?.limit }} />
                </div>
            </>
        ) : null,
    ];

    const renderHistory = history => history?.length ? [
        <Fragment key="history">
            <MenuHeader>
                <Translate content="recentSearch" />
            </MenuHeader>
        </Fragment>,
        renderSearchItems(history.map(item => ({
            ...item,
            label: (
                <Button
                    className="main-header-search-result__item-remove"
                    ghost
                    icon="cross-mark"
                    tooltip={<Translate content="removeFromRecent" />}
                    onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        searchTermRef.current.focus();
                        deleteRecentSearchResult({ variables: { input: { link: item.link } } });
                    }}
                />
            ),
        })), 'searchResultRecent'),
    ] : (
        <div className="main-header-search-result__note">
            <Status intent="info">
                <Hint><Translate content="recentSearchHint" /></Hint>
            </Status>
        </div>
    );

    const renderResults = () => {
        if (!isSearchFocused || (isSearchFocused && !((history) || results))) {
            return null;
        }

        let itemsToRender = results;
        if (itemsToRender && config?.knowledgeSearch?.enabled && knowledges && knowledges.length != 0) {
            itemsToRender = {
                ...results,
                records: [...results.records, ...knowledges],
            };
        }

        return (
            <Menu
                id="searchResultsBlock"
                className="main-header-search-result"
                ref={menuRef}
                role="listbox"
            >
                {itemsToRender ? renderSearchResults(itemsToRender) : null}
                {itemsToRender?.records.length === 0 ? (
                    <MenuDivider />
                ) : null}
                {(!itemsToRender || itemsToRender?.records.length === 0) ? renderHistory(history) : null}
            </Menu>
        );
    };

    const resetSearch = ({ resetValue = true } = {}) => {
        if (resetValue) {
            searchTermRef.current.value = '';
        }

        setSelectedItem(null);
        setResults(null);
        setKnowledges([]);

        abortPreviousSearch();
        abortKnowledgeSearch();
        setSearchInProgress({});
    };

    const onArrowKeyPressed = key => {
        const records = results && results.records.length ? results.records : history;
        if (!records?.length) {
            return;
        }

        if (selectedItem === null) {
            setSelectedItem(0);
            return;
        }

        if (keyCode.DOWN_ARROW === key && selectedItem < records.length - 1) {
            setSelectedItem(selectedItem + 1);
        }

        if (keyCode.UP_ARROW === key && selectedItem > 0) {
            setSelectedItem(selectedItem - 1);
        }
    };

    const shouldRunSearch = (termRef, term) => {
        if (termRef.current === term || term.length < 3) {
            return false;
        }
        termRef.current = term;
        return true;
    };

    const findTerm = () => {
        const term = searchTermRef.current.value.trim();

        if (!shouldRunSearch(lastTermRef, term)) {
            onSearchComplete();
            return;
        }

        pendingRequest.current = new Ajax.Request(
            url,
            {
                method: 'get',
                parameters: { term: searchTermRef.current.value.trim() },
                onSuccess: onSearchSuccess,
                onFailure: onSearchFailure,
                onComplete: onSearchComplete,
            },
        );
    };

    const findKnowledgeTerm = () => {
        const term = searchTermRef.current.value.trim();

        if (!shouldRunSearch(lastKnowledgeTermRef, term)) {
            clearSearchFlag('knowledge');
            return;
        }

        if (config?.knowledgeSearch?.enabled) {
            knowledgesRequestAbortControllerRef.current = new AbortController();

            getKnowledges({ variables: { question: term }, context: { noBatch: true, fetchOptions: { signal: knowledgesRequestAbortControllerRef.current.signal } },
            }).then(resp => {
                const knowledges = resp?.data.knowledgeSearch.knowledges;
                const knowledgeResults = (knowledges?.items || []).map(item => ({
                    link: item.url,
                    title: item.title,
                    details: '',
                    target: '_blank',
                    icon: 'images/search.png',
                    label: '',
                }));
                setKnowledges(knowledgeResults);
                knowledgesRequestAbortControllerRef.current = null;
                clearSearchFlag('knowledge');
            }).catch(() => {
                setKnowledges(null);
                knowledgesRequestAbortControllerRef.current = null;
                clearSearchFlag('knowledge');
            });
        } else {
            clearSearchFlag('knowledge');
        }
    };

    const onDocumentTouchStart = useCallback(() => {
        touchMoved.current = false;
    }, [touchMoved]);
    const onDocumentTouchMove = useCallback(() => {
        touchMoved.current = true;
    }, [touchMoved]);
    const onDocumentTouchEnd = useCallback(() => {
        if (touchMoved.current) {
            return;
        }

        searchTermRef.current.blur();

        removeTouchEventListeners({ onDocumentTouchStart, onDocumentTouchMove, onDocumentTouchEnd });
    }, [touchMoved, searchTermRef, onDocumentTouchStart, onDocumentTouchMove]);

    const searchPlaceholder = useMemo(() => {
        if (isSearchFocused) {
            if (isAdmin) {
                return Locale.getSection('components.search-bar').lmsg('fieldStubHint');
            }
            return null;
        }
        return Locale.getSection('components.search-bar').lmsg('fieldStub');
    }, [isAdmin, isSearchFocused]);

    useEffect(() => () => {
        if (resetTimeout.current) {
            clearTimeout(resetTimeout.current);
        }

        removeTouchEventListeners({ onDocumentTouchStart, onDocumentTouchMove, onDocumentTouchEnd });
    }, [onDocumentTouchEnd, onDocumentTouchMove, onDocumentTouchStart]);

    return (
        <div
            className="main-header-search"
            onTouchEnd={e => e.stopPropagation()}
        >
            <div
                className={classNames('main-header-search__group', isSearchFocused && 'search-focused')}
                role="search"
                aria-label={Locale.getSection('components.search-bar').lmsg('globalSearch')}
            >
                <Input
                    innerRef={searchTermRef}
                    id="searchTerm"
                    type="text"
                    className="main-header-search__control"
                    autoComplete="off"
                    placeholder={searchPlaceholder}
                    onPaste={handlePaste}
                    onKeyUp={handleKeyUp}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    prefix={isAnySearchInProgress(searchInProgress) ? (
                        <span className="main-header-search__loader" />
                    ) : <Icon name="search" />}
                    suffix={(
                        <Icon
                            name="cross-mark"
                            onClick={e => e.stopPropagation()}
                        />
                    )}
                    size="fill"
                    role="combobox"
                    aria-label={Locale.getSection('components.search-bar').lmsg(isAdmin ? 'fieldStubHint' : 'fieldStub')}
                    aria-controls="searchResultsBlock"
                    aria-autocomplete="list"
                    aria-expanded={isSearchFocused && (results?.records.length || history?.length) ? 'true' : 'false'}
                    aria-activedescendant={selectedItem !== null ? `searchResultItem-${selectedItem}` : undefined}
                />
                {renderResults()}
            </div>
        </div>
    );
};


MainHeaderSearch.propTypes = {
    url: PropTypes.string.isRequired,
};

export default MainHeaderSearch;
